import { useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import { Modal, ActionModal, Text, Spacer, FormField, AcknowledgeModal, CurrencyInput } from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation } from '@percent/partner-dashboard/common/hooks'
import { formatMoney } from '@percent/utility'
import { EmployeesModalProps } from './EmployeesModal.types'

export function UpdateMatchingBudgetModal({ isOpen, onClose, employee, refresh }: Readonly<EmployeesModalProps>) {
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()
  const { workplaceGivingService } = useServices()

  const [{ isLoading, data }, { apiFunc }] = useMutation(
    workplaceGivingService.updateMatchingBudget,
    () => {
      onClose()
      setIsSuccess(true)
      resetForm()
    },
    () => {
      onClose()
      setIsError(true)
      resetForm()
    }
  )

  const handleCloseSuccessModal = () => {
    setIsSuccess(false)
    refresh()
  }

  const handleCloseErrorModal = () => {
    setIsError(false)
  }

  const { errors, touched, dirty, values, handleSubmit, isValid, setFieldValue, resetForm } = useFormik({
    initialValues: {
      amount: undefined
    },
    validationSchema: Yup.object().shape({
      amount: Yup.number().required(t('errorMessage.required'))
    }),
    onSubmit: ({ amount }) => {
      apiFunc({
        accountId: employee.id,
        total: {
          amount: amount!,
          currency: employee.currency
        }
      })
    }
  })

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <ActionModal
          title={t('dialog.employees.matching.title')}
          primaryButtonText={t('button.updateBudget')}
          secondaryButtonText={t('button.cancel')}
          variant={isValid && dirty ? 'primary' : 'secondary'}
          disabled={!(isValid && !isLoading && dirty)}
          loading={isLoading}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          primaryBtnTestId="reject-request"
          secondaryBtnTestId="cancel-request"
          aria-labelledby="add-matching-balance-modal"
        >
          <Text size="small">
            {employee.fullName
              ? t('dialog.employees.matching.subTitleWithPossessive', {
                  name: employee.fullName,
                  email: employee.email,
                  totalBudget:
                    employee?.matching?.total?.amount &&
                    formatMoney(employee.matching.total, {
                      locale,
                      hideDecimalsIfZero: true
                    })
                })
              : t('dialog.employees.matching.subTitleWithoutPossessive', {
                  email: employee.email,
                  totalBudget:
                    employee?.matching?.total?.amount &&
                    formatMoney(employee.matching.total, {
                      locale,
                      hideDecimalsIfZero: true
                    })
                })}
          </Text>
          <form onSubmit={handleSubmit}>
            <Spacer size={4} axis="vertical" />
            <FormField
              label={t('dialog.employees.matching.label')}
              necessity="required"
              status={errors.amount ? 'danger' : 'default'}
              statusMessage={errors.amount}
              description={
                values.amount || values.amount === 0
                  ? t('dialog.employees.matching.description', {
                      remainingBudget: formatMoney(
                        {
                          amount: Math.max(0, values.amount - (employee.matching?.used?.amount ?? 0)),
                          currency: employee.currency
                        },
                        {
                          locale,
                          hideDecimalsIfZero: true
                        }
                      )
                    })
                  : ''
              }
              data-testid="amountInput"
            >
              <CurrencyInput
                id="amount"
                name="amount"
                status={touched.amount && errors.amount ? 'danger' : 'default'}
                value={values?.amount ? { amount: values.amount, currency: employee.currency } : undefined}
                onValueChange={value => setFieldValue('amount', value?.amount)}
                currency={employee.currency}
                locale={locale}
                allowNegativeValue={false}
                placeholder={{ amount: 1000, currency: employee.currency }}
              />
            </FormField>
            <Spacer size={4} axis="vertical" />
          </form>
        </ActionModal>
      </Modal>
      {data && (
        <Modal open={isSuccess} onClose={handleCloseSuccessModal} aria-labelledby="add-matching-balance-success">
          <AcknowledgeModal
            result="positive"
            title={t('dialog.employees.matching.success.title', {
              name: employee?.fullName || employee.email,
              totalBudget: formatMoney(data.data.data.state.total, {
                locale,
                hideDecimalsIfZero: true
              })
            })}
            description={t('dialog.employees.matching.success.description', {
              totalBudget: formatMoney(data.data.data.state.total, {
                locale,
                hideDecimalsIfZero: true
              })
            })}
            buttonText={t('button.close')}
            handleClose={handleCloseSuccessModal}
            buttonTestId="success-modal-btn"
          />
        </Modal>
      )}

      <Modal open={isError} onClose={handleCloseErrorModal} aria-labelledby="add-matching-balance-error">
        <AcknowledgeModal
          result="negative"
          title={t('dialog.employees.matching.error.title')}
          description={t('dialog.error.description')}
          buttonText={t('button.close')}
          handleClose={handleCloseErrorModal}
          buttonTestId="error-modal-btn"
        />
      </Modal>
    </>
  )
}
